/**
 * API client for the video-detail screen (video overview + review queue): the video
 * summary read already shipped (app/api/v1/videos.py's GET /{id}, verified against
 * schemas/videos.py's VideoSummaryResponse/RunStatusResponse) plus the findings read and
 * the four write routes a sibling agent is landing this same wave -- PATCH and the three
 * POST .../{approve,reject,reset} actions. Their exact shapes and paths are not a guess:
 * `app/schemas/findings.py`'s `UpdateFindingRequest` and `ReviewActionRequest` docstrings
 * name the routes verbatim ("Body for PATCH /videos/{video_id}/findings/{finding_id}",
 * "Body for POST .../findings/{finding_id}/{approve,reject,reset}"), so this client codes
 * against that contract even though the routes are not mounted in this repo yet.
 *
 * A new module rather than an addition to services/videosApi.js or services/api.js: both
 * belong to other phases' file sets (this phase's YOUR FILES list grants only
 * services/findingsApi.js), and every other phase module in this client already gets its
 * own file (standardsApi.js, videosApi.js) rather than growing services/api.js.
 *
 * `getVideo` lives here rather than in services/videosApi.js for the same reason: that
 * file is owned by a different phase and this phase cannot write to it, but the video
 * header strip and the overview page both need the full VideoSummaryResponse (file_url,
 * answer_key, nested run) that only GET /videos/{id} returns -- videosApi.js's own
 * `getStatus` returns just the RunStatusResponse shape.
 */
import api from './api';

export const findingsAPI = {
  // GET /videos/{video_id} (app/api/v1/videos.py, already mounted per this task's brief).
  getVideo: (videoId) => api.get(`/api/v1/videos/${videoId}`),

  // GET /videos/{video_id}/findings (already mounted). `includeNoIssue` maps to the
  // route's `include_no_issue` query flag. The review queue asks for them: on an arbitrary
  // video most groups map to no criterion at all, and "0 findings" alone reads as a broken
  // pipeline. The no-issue list is what shows the model DID look and judged the frames
  // compliant.
  //
  // A sibling wave adds `observations` to this same response (`FindingListResponse`,
  // additive alongside the existing `findings`/`no_issue` arrays) -- every group the
  // pipeline formed, in walkthrough order, built through the identical
  // `report_service._build_observations` the report reads, each finding-backed entry
  // additionally carrying the `Finding` UUID as `id`. `ReviewQueuePage` renders that field
  // through `ObservationEntry` rather than re-deriving `outcome.code` client-side; no
  // change needed here since this call already returns the response body verbatim.
  getFindings: (videoId, { includeNoIssue = false } = {}) =>
    api.get(`/api/v1/videos/${videoId}/findings`, {
      params: includeNoIssue ? { include_no_issue: true } : undefined,
    }),

  // GET /videos/{video_id}/findings/{finding_id} (already mounted).
  getFinding: (videoId, findingId) =>
    api.get(`/api/v1/videos/${videoId}/findings/${findingId}`),

  // PATCH /videos/{video_id}/findings/{finding_id} -- UpdateFindingRequest's four editable
  // fields (criterion_id, problem_identified, standard_suggestion, auditor_notes); the
  // caller sends only the keys that actually changed (plan 6.4's FindingEditForm contract).
  updateFinding: (videoId, findingId, patch) =>
    api.patch(`/api/v1/videos/${videoId}/findings/${findingId}`, patch),

  // POST .../findings/{finding_id}/approve|reject|reset -- ReviewActionRequest's only
  // field is an optional auditor_notes, so `body` defaults to {} rather than requiring
  // every caller to pass one.
  approveFinding: (videoId, findingId, body = {}) =>
    api.post(`/api/v1/videos/${videoId}/findings/${findingId}/approve`, body),

  rejectFinding: (videoId, findingId, body = {}) =>
    api.post(`/api/v1/videos/${videoId}/findings/${findingId}/reject`, body),

  resetFinding: (videoId, findingId, body = {}) =>
    api.post(`/api/v1/videos/${videoId}/findings/${findingId}/reset`, body),
};

export default findingsAPI;
